<template>
  <Transition
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="transform translate-x-2 opacity-0"
    enter-to-class="transform translate-x-0 opacity-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="transform translate-x-0 opacity-100"
    leave-to-class="transform translate-x-2 opacity-0">
    <div
      v-if="showPathFlow && pathFlowData"
      class="absolute top-10 right-10 z-50 w-80 max-h-[600px] overflow-y-auto rounded-lg bg-white p-4 shadow-lg dark:bg-slate-800">
      <div class="mb-4 flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300">
          Visitor Flow Analytics
        </h3>
        <button
          @click="togglePathFlow"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>
      
      <!-- Top Paths Section -->
      <div v-if="topPaths.length > 0" class="mb-6">
        <h4 class="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-400">
          Most Common Paths
        </h4>
        <div class="space-y-2">
          <div
            v-for="path in topPaths"
            :key="`${path.from}-${path.to}`"
            class="flex items-center justify-between rounded bg-gray-50 p-2 dark:bg-slate-700">
            <div class="flex items-center space-x-2 text-sm">
              <span class="font-medium text-gray-700 dark:text-gray-300">
                {{ getNodeLabel(path.from) }}
              </span>
              <ArrowRightIcon class="h-3 w-3 text-gray-400" />
              <span class="font-medium text-gray-700 dark:text-gray-300">
                {{ getNodeLabel(path.to) }}
              </span>
            </div>
            <span class="text-sm font-bold text-purple-600 dark:text-purple-400">
              {{ path.count.toLocaleString() }}
            </span>
          </div>
        </div>
      </div>
      
      <!-- Drop-off Points Section -->
      <div v-if="topDropOffs.length > 0" class="mb-6">
        <h4 class="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-400">
          Top Drop-off Points
        </h4>
        <div class="space-y-2">
          <div
            v-for="dropOff in topDropOffs"
            :key="`dropoff-${dropOff.nodeId}`"
            class="flex items-center justify-between rounded bg-red-50 p-2 dark:bg-red-900/20">
            <div class="flex items-center space-x-2 text-sm">
              <ExclamationTriangleIcon class="h-4 w-4 text-red-500" />
              <span class="font-medium text-gray-700 dark:text-gray-300">
                {{ getNodeLabel(dropOff.nodeId) }}
              </span>
            </div>
            <span class="text-sm font-bold text-red-600 dark:text-red-400">
              {{ dropOff.count.toLocaleString() }} exits
            </span>
          </div>
        </div>
      </div>
      
      <!-- Summary Statistics -->
      <div class="rounded bg-gray-50 p-3 dark:bg-slate-700">
        <h4 class="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-400">
          Flow Summary
        </h4>
        <div class="space-y-1 text-xs">
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Total Paths:</span>
            <span class="font-medium text-gray-700 dark:text-gray-300">
              {{ pathFlowData.paths.length }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Total Traffic:</span>
            <span class="font-medium text-gray-700 dark:text-gray-300">
              {{ totalTraffic.toLocaleString() }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Drop-off Rate:</span>
            <span class="font-medium text-red-600 dark:text-red-400">
              {{ dropOffRate }}%
            </span>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { ArrowRightIcon, XMarkIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import type { PathFlow, DropOff } from '../services/flowMetricsService'

const flowBuilderStore = useFlowBuilderStore()
const flowMetricsStore = useFlowMetricsStore()
const { showPathFlow } = storeToRefs(flowBuilderStore)
const { togglePathFlow } = flowBuilderStore

const props = defineProps<{
  flowId: string
}>()

// Get path flow data
const pathFlowData = computed(() => {
  return flowMetricsStore.getPathFlowData(props.flowId)
})

// Get top 5 paths by traffic
const topPaths = computed(() => {
  if (!pathFlowData.value?.paths) return []
  return pathFlowData.value.paths.slice(0, 5)
})

// Get top 5 drop-off points
const topDropOffs = computed(() => {
  if (!pathFlowData.value?.dropOffs) return []
  return pathFlowData.value.dropOffs.slice(0, 5)
})

// Calculate total traffic
const totalTraffic = computed(() => {
  if (!pathFlowData.value?.paths) return 0
  return pathFlowData.value.paths.reduce((sum, path) => sum + path.count, 0)
})

// Calculate drop-off rate
const dropOffRate = computed(() => {
  if (!pathFlowData.value?.dropOffs || totalTraffic.value === 0) return '0'
  const totalDropOffs = pathFlowData.value.dropOffs.reduce((sum, d) => sum + d.count, 0)
  return ((totalDropOffs / (totalTraffic.value + totalDropOffs)) * 100).toFixed(1)
})

// Get node label from ID (simplified for now)
const getNodeLabel = (nodeId: string) => {
  // Extract node type from ID if possible
  if (nodeId.includes('_')) {
    const parts = nodeId.split('_')
    const type = parts[0]
    
    // Map node types to friendly names
    const typeLabels: Record<string, string> = {
      'sg': 'Question',
      'offer': 'Offer',
      'redirect': 'Redirect',
      'yesno': 'Yes/No',
      'message': 'Message',
      'data': 'Data',
      'code': 'Code',
      'exit': 'Exit',
      'highscore': 'Score Split',
      'random': 'Random Split'
    }
    
    return typeLabels[type] || type
  }
  
  // Try to find the node in the flow
  const node = findNodeById(nodeId)
  if (node) {
    if (node.type === 'sg' && node.ask) {
      return 'Question'
    }
    return node.type.charAt(0).toUpperCase() + node.type.slice(1)
  }
  
  return nodeId
}

// Helper to find node by ID in the flow
const findNodeById = (nodeId: string) => {
  // This would need access to the flow definition
  // For now, returning null
  return null
}
</script>